import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Browser } from "@capacitor/browser";
import { Clipboard } from '@capacitor/clipboard';
import { AlertController, IonSpinner, ModalController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { AccountType, ApiModules, ApiOperations, DisplayService, HideForPlatformDirective, ImageUtil, UpdateLog } from '@qid/core';
import { SexyModule } from "@qid/sexy";
import { AvatarRoundComponent, SuperBackButtonComponent, SuperButtonComponent } from '@qid/super';
import { register } from 'swiper/element/bundle';
import { SharesDataRepository } from "../../+state/shares-data-repository.service";
import { ImageViewerService } from '../../../../components/image-viewer/image-viewer.service';
import { RoleAccessDirective } from '../../../../directives/role-access.directive';
import { ID } from "../../../../models/ID";
import { FormatPhonePipe } from "../../../../pipes/format-phone.pipe";
import { AuthSelectors } from '../../../authentication';
import { AccountShareRepositoryService } from '../../../account-share';
import { AgreementViewComponent } from '../../../collection-template';
import { ViewIdPage, ViewMultipleDocumentsPage } from "../../../id";
import { IdCardComponent } from '../../../id/compenents/id-card/id-card.component';
import { Organisation } from "../../../organisation";
import { PermissionDirective } from '../../../organisation-user';
import { User, UserService } from "../../../user";
import { AddUserRatingComponent, ViewUserRatingPage } from "../../../user-rating";
import { ShareCheckinViewComponent } from '../../components/share-checkin-view/share-checkin-view.component';
import { ParticipantActionsSheetComponent } from '../../components/participant-actions-sheet/participant-actions-sheet.component';
import { ParticipantRoles } from "../../enum/participant-roles.enum";
import { ShareStatus } from '../../enum/share-status.enum';
import { Participant } from "../../model/participant";
import { Share } from '../../model/Share';
import { ExportPdfService } from "../../services/export-pdf.service";
import { ShareService } from '../../services/share.service';

register();

@Component({
  selector: 'app-view-share',
  templateUrl: './view-share.page.html',
  styleUrls: ['./view-share.page.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    SuperButtonComponent,
    AvatarRoundComponent,
    SexyModule,
    HideForPlatformDirective,
    RoleAccessDirective,
    PermissionDirective,
    FormatPhonePipe,
    IdCardComponent,
    ShareCheckinViewComponent,
    AgreementViewComponent,
    SuperBackButtonComponent,
    IonSpinner
  ]
})
export class ViewSharePage implements OnInit {

  @Input() shareId: string;

  public share: Share = null;
  public organiser: Participant;
  public sharerParticipants: Participant[] = [];
  public participant: Participant;
  public baseIconUrl = 'assets/imgs/'

  // ponytail: one store read for org gate; role/permission matrix if needed later
  private readonly activeAccountType = toSignal(
    inject(Store).select(AuthSelectors.selectActiveAccountType),
  );

  constructor(
    private alertController: AlertController,
    private shareService: ShareService,
    private shareDataRepository: SharesDataRepository,
    public modalController: ModalController,
    private exportPdfService: ExportPdfService,
    private userService: UserService,
    private imageViewerService: ImageViewerService,
    private accountShareRepository: AccountShareRepositoryService,
    private displayService: DisplayService,
  ) {
  }

  async copyToClipboard(value: string) {
    if (!value) return;
    await Clipboard.write({ string: value });
    this.displayService.toast({ message: 'Copied to clipboard' });
  }

  ngOnInit() {
  }

  ionViewDidEnter() {
    if (this.shareId) {
      this.onShareIdFetched();
    }
  }

  private async onShareIdFetched() {

    await this.fetchShare(this.shareId)

    this.sharerParticipants = []

    if (!this.share?.participants) {
      return
    }

    for (let participant of this.share.participants) {
      if (participant.roles.indexOf(ParticipantRoles.organiser) > -1) {
        this.organiser = participant;
        continue;
      }
      if (!participant.roles || participant.roles.indexOf(ParticipantRoles.sharer) > -1) {
        this.sharerParticipants.push(participant)
      }
    }

    this.fetchUserRatings();

  }

  get logsWithRemarks(): UpdateLog[] {
    return (this.share?.updateLogs ?? [])
      .filter((log) => log?.remark)
      .slice()
      .reverse();
  }

  getParticipantRole(roles: string[]) {
    if (!roles) {
      return
    }
    if (roles.indexOf(ParticipantRoles.organiser) > -1) {
      return 'Organiser'
    }
    if (roles.indexOf(ParticipantRoles.admin) > -1) {
      return 'Admin'
    }

    return 'Participant'
  }

  //Api Calls

  async fetchShare(id: string) {
    if (!id) {
      return;
    }
    const response = await this.shareService.read(id);
    if (response && response.data) {
      this.share = response.data;
    }
  }

  delete = async () => {
    try {
      const res = await this.shareDataRepository.delete(this.share._id);
      if (res?.data) {
        this.accountShareRepository.deleteRecord(this.share._id);
        this.modalController.dismiss()
      }
    } catch (e) {
    }
  };

  //Component Actions

  async openViewIdPage(id: ID) {

    if (!id) {
      return
    }

    const modal = await this.modalController.create({
      component: ViewIdPage,
      componentProps: {
        id: id,
      },
      animated: false,
    });
    modal.present();
  }

  printAsPDF = async () => {
    await this.exportPdfService.exportIndividualPdf(this.share);
  };

  deleteShare = async () => {
    const alert = await this.alertController.create({
      header: 'Delete ID',
      message: 'Are you sure you want to delete this ID?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          handler: async () => {
            await this.delete();
          },
        },
      ],
    });
    await alert.present();
    await alert.onDidDismiss();
  };

  async openViewAllDocumentsPage(document_ids: string[]) {
    const modal = await this.modalController.create({
      component: ViewMultipleDocumentsPage,
      componentProps: {
        document_ids: document_ids
      },
    });
    modal.present();
  }

  getImage(url: string) {
    return ImageUtil.getImageSource(url);
  }

  closeModal() {
    this.modalController.dismiss()
  }

  async openWhatsapp(participant: User | Organisation | any) {
    await Browser.open({
      url: `https://wa.me/${participant?.phoneNumber}`
    });
  }

  onSelfieClicked(imageUrl: string) {
    this.imageViewerService.open([imageUrl])
  }

  async onWriteAReviewClicked() {
    const modal = await this.modalController.create({
      component: AddUserRatingComponent,
      componentProps: {
        participants: this.sharerParticipants,
        onRated: () => this.fetchUserRatings()
      }
    })

    await modal.present()
  }

  async fetchUserRatings() {
    let index = 0;
    for (let user of this.sharerParticipants) {
      const res = await this.userService.getUserById(user.account_id)
      if (res) {
        this.sharerParticipants[index].account = res
      }
      index++
    }

  }

  async openViewRatingModal(account) {
    const modal = await this.modalController.create({
      component: ViewUserRatingPage,
      componentProps: {
        user: account
      }
    })
    modal.present()
  }

  async openParticipantActions(participant: Participant) {
    const isOrg = this.activeAccountType() === AccountType.organisation;
    const isAdmin = participant.roles?.indexOf(ParticipantRoles.admin) > -1;

    const modal = await this.modalController.create({
      component: ParticipantActionsSheetComponent,
      componentProps: {
        participant,
        canMakeAdmin: isOrg && !!participant.account_id && !isAdmin,
        canDismissAdmin: isOrg && isAdmin,
        canRemove: isOrg && this.share?.status === ShareStatus.open && (this.share?.participants?.length ?? 0) > 1,
      },
      initialBreakpoint: 0.48,
      breakpoints: [0, 0.48],
      cssClass: 'participant-actions-sheet-modal',
    });
    await modal.present();

    const { data } = await modal.onWillDismiss<{ action?: string }>();
    if (!data?.action || !this.share?._id) return;

    const name = participant.account?.name || participant.accountSnapshot?.name || 'this member';
    if (data.action === 'makeAdmin') {
      const res = await this.shareService.makeParticipantAdmin(this.share._id, participant.account_id);
      if (res?.data) participant.roles = [...(participant.roles ?? []), ParticipantRoles.admin];
    } else if (data.action === 'dismissAdmin') {
      if (!(await this.confirmAction('Dismiss as admin?', `Remove admin rights from ${name}?`, 'Dismiss'))) return;
      const res = await this.shareService.dismissParticipantAdmin(this.share._id, participant.account_id);
      if (res?.data) participant.roles = (participant.roles ?? []).filter((r) => r !== ParticipantRoles.admin);
    } else if (data.action === 'remove') {
      if (!(await this.confirmAction('Remove member?', `Remove ${name} from this group?`, 'Remove'))) return;
      const participantIndex = (this.share.participants ?? []).findIndex((p) =>
        participant.account_id
          ? p.account_id === participant.account_id
          : p === participant,
      );
      if (participantIndex < 0) return;
      const res = await this.shareService.removeParticipant(this.share._id, participantIndex);
      if (res?.data) {
        this.share.participants = (this.share.participants ?? []).filter((_, i) => i !== participantIndex);
        this.sharerParticipants = this.sharerParticipants.filter((p) =>
          participant.account_id ? p.account_id !== participant.account_id : p !== participant,
        );
      }
    }
  }

  private async confirmAction(header: string, message: string, confirmText: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: confirmText, role: 'destructive' },
      ],
    });
    await alert.present();
    const { role } = await alert.onWillDismiss();
    return role === 'destructive';
  }

  protected readonly ApiOperations = ApiOperations;
  protected readonly ApiModules = ApiModules;
}
